import React from 'react';
import SaveSearchDialog from '../dialogs/SaveSearchDialog';
import SavedSearchesDialog from '../dialogs/SavedSearchesDialog';
import UpdateSearchDialog from '../dialogs/UpdateSearchDialog';
import DisclaimerDialog from '../dialogs/DisclaimerDialog';
import { SavedSearch } from '../../models';

interface DialogManagerProps {
  // Dialog visibility state
  saveSearchDialogOpen: boolean;
  showSavedSearches: boolean;
  updateSearchDialogOpen: boolean;
  disclaimerDialogOpen?: boolean;
  
  // Data for dialogs
  savedSearches: SavedSearch[];
  currentLoadedSearchName: string;
  
  // These functions are no longer used by the dialogs as they now use the SearchContext directly
  // but we keep them for backward compatibility with the AppContent component
  onSaveSearchWithName: (name: string, autoSave?: boolean) => Promise<boolean>;
  onLoadSavedSearch: (searchId: string) => any;
  onUpdateSearch: () => Promise<boolean>;
  onDeleteSavedSearch: (searchId: string) => boolean;
  
  // Dialog management functions
  onCloseSaveSearchDialog: () => void;
  onCloseSavedSearchesDialog: () => void;
  onCloseUpdateSearchDialog: () => void;
  onCloseDisclaimerDialog?: () => void;
  requireDisclaimerAcknowledgment?: boolean;
}

/**
 * DialogManager component
 * Centralizes all dialog rendering to simplify the main app component
 */
const DialogManager: React.FC<DialogManagerProps> = ({
  // Dialog visibility
  saveSearchDialogOpen,
  showSavedSearches,
  updateSearchDialogOpen,
  disclaimerDialogOpen = false,
  
  // Data and handlers (not directly needed by dialogs anymore, but kept for compatibility)
  savedSearches,
  currentLoadedSearchName,
  onSaveSearchWithName,
  onLoadSavedSearch,
  onUpdateSearch,
  onDeleteSavedSearch,
  
  // Close handlers
  onCloseSaveSearchDialog,
  onCloseSavedSearchesDialog,
  onCloseUpdateSearchDialog,
  onCloseDisclaimerDialog = () => {},
  requireDisclaimerAcknowledgment = false
}) => {
  return (
    <>
      {/* Save Search Dialog - now using SearchContext directly */}
      <SaveSearchDialog
        open={saveSearchDialogOpen}
        onClose={onCloseSaveSearchDialog}
      />

      {/* Saved Searches Dialog - now using SearchContext directly */}
      <SavedSearchesDialog
        open={showSavedSearches}
        onClose={onCloseSavedSearchesDialog}
      />

      {/* Update Search Dialog - now using SearchContext directly */}
      <UpdateSearchDialog
        open={updateSearchDialogOpen}
        onClose={onCloseUpdateSearchDialog}
        searchName={currentLoadedSearchName}
      />

      {/* Disclaimer Dialog */}
      <DisclaimerDialog
        open={disclaimerDialogOpen || false}
        onClose={onCloseDisclaimerDialog || (() => {})}
        requireAcknowledgment={requireDisclaimerAcknowledgment}
      />
    </>
  );
};

export default DialogManager;
